import styled, { css } from 'styled-components';

const PRIMARY = '#e71037';

interface ContainerProps {
  isFocused: boolean;
  disabled?: boolean;
  error?: boolean;
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  color: #4a4b4a;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  label {
    font-size: 12px;
    color: ${({ error, theme: { colors } }) =>
      error ? colors.error : '#757575'};
    ${({ isFocused }) =>
      isFocused &&
      css`
        color: ${PRIMARY};
      `}
  }

  > div:first-of-type {
    border-bottom: 1px solid
      ${({ error, theme: { colors } }) => (error ? colors.error : '#afafaf')};
    ${({ isFocused }) =>
      isFocused &&
      css`
        border-bottom-color: ${PRIMARY};
      `}
  }
`;

export const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 2px;

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    padding: 0;
    background: transparent;
    font-family: inherit;
    font-size: 16px;
    line-height: 24px;
    color: #4a4b4a;

    &::placeholder {
      color: #afafaf;
    }
  }
`;

export const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  :hover,
  :focus-visible {
    color: ${PRIMARY};
  }
`;

export const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 260;
  width: 280px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 7px 0px rgba(201, 201, 201, 0.5),
    0 10px 26px rgba(0, 0, 0, 0.08);
`;

export const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 12px;

  strong {
    flex: 1;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #4a4b4a;
    cursor: pointer;

    :hover {
      background: #f1f1f1;
    }
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

export const WeekDay = styled.span`
  padding: 4px 0;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #757575;
`;

interface DayProps {
  outside: boolean;
  isToday: boolean;
  selected: boolean;
}

export const Day = styled.button<DayProps>`
  height: 34px;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  color: ${({ outside }) => (outside ? '#c1c1c1' : '#4a4b4a')};
  cursor: pointer;

  :hover:not(:disabled) {
    background: #f1f1f1;
  }

  ${({ isToday }) =>
    isToday &&
    css`
      border-color: ${PRIMARY};
      color: ${PRIMARY};
      font-weight: 600;
    `}

  ${({ selected }) =>
    selected &&
    css`
      background: ${PRIMARY};
      border-color: ${PRIMARY};
      color: #fff;
      font-weight: 600;

      :hover:not(:disabled) {
        background: ${PRIMARY};
      }
    `}

  :disabled {
    color: #e0e0e0;
    cursor: not-allowed;
  }
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f1f1f1;

  button {
    padding: 4px 8px;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: ${PRIMARY};
    cursor: pointer;

    :disabled {
      color: #c1c1c1;
      cursor: not-allowed;
    }
  }
`;

export const ErrorMessage = styled.span`
  position: absolute;
  bottom: -18px;
  color: ${({ theme }) => theme.colors.error};
  font-size: 12px;
`;
